import { useState, useEffect } from "react";

const LIMIT = 10;

/**
 * Generic accordion hook — works for ANY role (photographer, videographer, editor, lighting).
 *
 * @param {object} params
 * @param {boolean}  params.isOpen        - Modal open state
 * @param {string}   params.bookingId     - Current booking ID
 * @param {Function} params.fetchListAPI  - API fn: (page, limit, bookingId) => Promise
 * @param {Function} params.fetchAllAPI   - API fn: (1, total, bookingId) => Promise  (for select-all)
 * @param {Function} [params.fetchSortAPI]- API fn: (filters, page, limit, bookingId) => Promise (optional)
 */
const useRoleAccordion = ({
    isOpen,
    bookingId,
    fetchListAPI,
    fetchAllAPI,
    fetchSortAPI = null,
    selectedPackageIndex,
    videoPackages = [],
    roleKey,
    refreshVersion = 0,
    isExpanded = false,
}) => {
    const [items, setItems]                   = useState([]);
    const [search, setSearch]                 = useState("");
    const [selectAllChecked, setSelectAllChecked] = useState(false);
    const [loading, setLoading]               = useState(false);
    const [isLock, setIsLock]                 = useState(false);
    const [page, setPage]                     = useState(1);
    const [total, setTotal]                   = useState(0);
    const [filters, setFilters]               = useState({ rating: "", expertise: "", category: "" });
    const [selectedIds, setSelectedIds]       = useState([]);

    const totalPages = Math.ceil(total / LIMIT);

    // ─── Derived: client-side search filter ──────────────────────────────────────
    const filteredItems = items.filter((p) => {
        const matchesSearch = p.name?.toLowerCase().includes(search.toLowerCase()) ||
                              (p.mobileNumber || p.phone)?.includes(search);
        
        let matchesCategory = true;
        let matchesExpertise = true;
        let matchesRating = true;

        if (!fetchSortAPI) {
            if (filters.category) {
                matchesCategory = p.categories && p.categories.includes(filters.category);
            }
            if (filters.expertise) {
                matchesExpertise = (p.expertiseLevel || p.level) === filters.expertise.toUpperCase();
            }
            if (filters.rating) {
                matchesRating = (p.avgRating || 0) >= parseFloat(filters.rating);
            }
        }

        return matchesSearch && matchesCategory && matchesExpertise && matchesRating;
    });

    // ─── Fetch list ───────────────────────────────────────────────────────────────
    const fetchItems = async (pageNo = page) => {
        if (!isExpanded) return;
        try {
            setLoading(true);
            let res;

            const hasFilter = fetchSortAPI && (filters.rating || filters.expertise || filters.category);
            if (hasFilter) {
                res = await fetchSortAPI(filters, pageNo, LIMIT, bookingId);
            } else {
                res = await fetchListAPI(pageNo, LIMIT, bookingId);
            }

            setItems(res?.data?.data || []);
            setTotal(res?.data?.meta?.total ?? res?.data?.data?.length ?? 0);
            setPage(res?.data?.meta?.page || pageNo);
            setIsLock(res?.data?.isLock || false);
        } catch (err) {
            console.error("Fetch failed", err);
        } finally {
            setLoading(false);
        }
    };

    // ─── Effects ──────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!isOpen || !isExpanded) return;
        fetchItems(page);
        setSelectAllChecked(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, page, refreshVersion, isExpanded]);

    useEffect(() => {
        if (!isOpen || !isExpanded) return;
        fetchItems(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, isOpen, isExpanded]);

    const currentPkgType = (videoPackages && videoPackages[selectedPackageIndex] && videoPackages[selectedPackageIndex].type) ? videoPackages[selectedPackageIndex].type.toLowerCase() : "";

    // Reset when plan changes
    useEffect(() => {
        reset();
        
        // Auto-select expertise filter based on package type for Editors
        if (roleKey === "editor" && (currentPkgType === "standard" || currentPkgType === "premium")) {
            setFilters(prev => {
                if (prev.expertise === currentPkgType) return prev; // Avoid setting if same
                return { ...prev, expertise: currentPkgType };
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedPackageIndex, currentPkgType, roleKey]);

    // ─── Handlers ─────────────────────────────────────────────────────────────────
    const handleSelectAll = async (e, maxAllowed = Infinity, excludedIds = []) => {
        const checked = e.target.checked;
        setSelectAllChecked(checked);

        if (checked) {
            try {
                const res = await fetchAllAPI(1, total, bookingId);
                let allItems = res?.data?.data || [];
                // Exclude inactive photographers
                let activeItems = allItems.filter(p => p.status?.toLowerCase() !== 'inactive' && p.isActive !== false);
                let allIds = activeItems.map((p) => p._id);
                
                // Filter out excluded IDs
                allIds = allIds.filter(id => !excludedIds.includes(id));
                
                // Limit to maxAllowed
                if (allIds.length > maxAllowed) {
                    allIds = allIds.slice(0, maxAllowed);
                }
                
                setSelectedIds(allIds);
            } catch (err) {
                console.error("Select all failed", err);
            }
        } else {
            setSelectedIds([]);
        }
    };

    const handleToggle = (id, checked) => {
        setSelectedIds((prev) =>
            checked ? [...prev, id] : prev.filter((pid) => pid !== id)
        );
        if (selectAllChecked) setSelectAllChecked(false);
    };

    const handleFilterChange = (key, value) =>
        setFilters((prev) => ({ ...prev, [key]: value }));

    const resetFilters = () => {
        let defaultExpertise = "";
        if (roleKey === "editor" && (currentPkgType === "standard" || currentPkgType === "premium")) {
            defaultExpertise = currentPkgType;
        }
        setFilters({ rating: "", expertise: defaultExpertise, category: "" });
        setSearch("");
        setPage(1);
    };

    const reset = () => {
        setSelectedIds([]);
        setSelectAllChecked(false);
        setSearch("");
        resetFilters();
    };

    return {
        filteredItems,
        search,
        setSearch,
        selectAllChecked,
        loading,
        isLock,
        page,
        setPage,
        total,
        totalPages,
        filters,
        selectedIds,
        LIMIT,
        // Handlers
        handleSelectAll,
        handleToggle,
        handleFilterChange,
        resetFilters,
        reset,
    };
};

export default useRoleAccordion;